import html2canvas from 'html2canvas';

// Types and interfaces for enhanced functionality (Interface Segregation Principle)
interface WatermarkOptions {
  url?: string;
  opacity?: number;
  size?: { width: number; height: number };
  positionY?: 'twoFifths' | 'center' | 'top'; // New: Y positioning strategy
}

interface DownloadOptions {
  watermarkOptions?: WatermarkOptions;
  useCanvasWatermark?: boolean;
  includeTimestamp?: boolean;
  disableWatermark?: boolean;
}

interface ChartElement {
  element: HTMLElement;
  title: string;
  filename: string;
}

// Constants (Dependency Inversion Principle - depend on abstractions)
// Use the locally-hosted high-resolution PNG stored in /public (space already encoded).
const DEFAULT_WATERMARK_URL = '/GovstreamAI%20Logo@4x.png';
const DEFAULT_SECTION_IDS = ['peer-comparison-charts-section', 'overtime-charts-section', 'snapshot-charts-section'];

// Enhanced SVG loading utility (Single Responsibility Principle)
// Renamed behaviour-wise: now returns the URL directly for raster formats, otherwise converts SVG.
const loadSVGAsDataURL = async (svgUrl: string): Promise<string> => {
  // Fast-path: if the source is already a raster image we can skip any canvas conversion.
  if (!svgUrl.trim().toLowerCase().endsWith('.svg')) {
    console.log('[Image Loader] Raster image detected, skipping SVG conversion');
    return svgUrl; // Return as-is to avoid double-encoding
  }
  return new Promise((resolve, reject) => {
    console.log(`[SVG Loader] Loading SVG from: ${svgUrl}`);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        console.log(`[SVG Loader] SVG loaded, dimensions: ${img.naturalWidth}x${img.naturalHeight}`);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.error('[SVG Loader] Could not get canvas context for SVG conversion');
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        console.log('[SVG Loader] Successfully converted SVG to data URL');
        resolve(dataUrl);
      } catch (error) {
        console.error('[SVG Loader] Failed to convert SVG to data URL:', error);
        reject(new Error(`Failed to convert SVG to data URL: ${error}`));
      }
    };
    
    img.onerror = (e) => {
      console.error('[SVG Loader] Failed to load SVG:', e);
      console.error('[SVG Loader] This could be due to CORS restrictions or network issues');
      reject(new Error('Failed to load SVG'));
    };
    img.src = svgUrl;
  });
};

// Canvas-based watermark utility (Single Responsibility Principle)
const addCanvasWatermark = async (
  canvas: HTMLCanvasElement, 
  options: WatermarkOptions = {}
): Promise<HTMLCanvasElement> => {
  const {
    url = DEFAULT_WATERMARK_URL,
    opacity = 0.5,
    size = { width: 1200, height: 360 },
    positionY = 'twoFifths'
  } = options;

  try {
    console.log(`[Watermark] Loading SVG from: ${url}`);
    // Load the SVG as a data URL first
    const watermarkDataUrl = await loadSVGAsDataURL(url);
    console.log('[Watermark] SVG loaded successfully, applying to canvas');
    
    return new Promise((resolve, reject) => {
      const watermarkImg = new Image();
      
      watermarkImg.onload = () => {
        try {
          console.log(`[Watermark Canvas Debug] Creating fresh canvas for watermarking...`);
          console.log(`[Watermark Canvas Debug] Original canvas: ${canvas.width}x${canvas.height}`);
          
          // Create a fresh canvas that we can definitely modify
          const freshCanvas = document.createElement('canvas');
          freshCanvas.width = canvas.width;
          freshCanvas.height = canvas.height;
          
          const ctx = freshCanvas.getContext('2d');
          if (!ctx) {
            console.error('[Watermark] Could not get canvas context for fresh canvas');
            reject(new Error('Could not get canvas context for fresh canvas'));
            return;
          }
          
          // Copy the original canvas content to our fresh canvas
          console.log(`[Watermark Canvas Debug] Copying original canvas content...`);
          ctx.drawImage(canvas, 0, 0);
          
          // Verify the copy worked
          const copyDataUrl = freshCanvas.toDataURL('image/png');
          console.log(`[Watermark Canvas Debug] Fresh canvas data URL length: ${copyDataUrl.length}`);
          
          // Capture before state for comparison
          const beforeDataUrl = freshCanvas.toDataURL('image/png');
          console.log(`[Watermark Canvas Debug] BEFORE watermark: Fresh canvas data URL length: ${beforeDataUrl.length}`);
          
          // Check canvas context state
          console.log(`[Watermark Canvas Debug] Canvas context state:`);
          console.log(`[Watermark Canvas Debug] - fillStyle: ${ctx.fillStyle}`);
          console.log(`[Watermark Canvas Debug] - strokeStyle: ${ctx.strokeStyle}`);
          console.log(`[Watermark Canvas Debug] - globalAlpha: ${ctx.globalAlpha}`);
          console.log(`[Watermark Canvas Debug] - globalCompositeOperation: ${ctx.globalCompositeOperation}`);
          
          console.log(`[Watermark Canvas Debug] Fresh canvas: ${freshCanvas.width}x${freshCanvas.height}`);
          console.log(`[Watermark Canvas Debug] Watermark image: ${watermarkImg.width}x${watermarkImg.height}`);
          console.log(`[Watermark Canvas Debug] Requested size: ${size.width}x${size.height}`);
          
          // Calculate aspect-ratio preserving size to prevent squishing
          const originalAspectRatio = watermarkImg.width / watermarkImg.height;
          const targetAspectRatio = size.width / size.height;
          
          let actualWidth, actualHeight;
          if (originalAspectRatio > targetAspectRatio) {
            // Image is wider, fit to width
            actualWidth = size.width;
            actualHeight = size.width / originalAspectRatio;
          } else {
            // Image is taller, fit to height  
            actualHeight = size.height;
            actualWidth = size.height * originalAspectRatio;
          }
          
          console.log(`[Watermark Canvas Debug] Aspect-corrected size: ${actualWidth}x${actualHeight}`);
          
          // Debug: Let's try different positioning strategies
          console.log(`[Watermark Canvas Debug] === POSITIONING ANALYSIS ===`);
          
          // Strategy 1: Mathematical center (current)
          const mathX = (freshCanvas.width - actualWidth) / 2;
          const mathY = (freshCanvas.height - actualHeight) / 2;
          console.log(`[Watermark Canvas Debug] Math center: (${mathX}, ${mathY})`);
          
          // Strategy 2: Absolute center regardless of watermark size
          const absoluteX = freshCanvas.width / 2;
          const absoluteY = freshCanvas.height / 2;
          console.log(`[Watermark Canvas Debug] Absolute center: (${absoluteX}, ${absoluteY})`);
          
          // Strategy 3: Try 25% from edges (common for visual centering)
          const visualX = freshCanvas.width * 0.25;
          const visualY = freshCanvas.height * 0.25;
          console.log(`[Watermark Canvas Debug] Visual center (25%): (${visualX}, ${visualY})`);
          
          // Center watermark in the middle column of 2-chart-wide layout
          // Horizontal: dead center between the two chart columns
          // Vertical: position based on section type for optimal visibility
          const targetCenterX = freshCanvas.width / 2;        // Dead center horizontally
          const targetCenterY = (() => {
            switch (positionY) {
              case 'top':
                return actualHeight / 2 + 40; // 40px padding from the top
              case 'center':
                return freshCanvas.height / 2; // Center (1/2) for snapshot view
              case 'twoFifths':
              default:
                return freshCanvas.height * 2 / 5; // 2/5 down for peer comparison and overtime
            }
          })();
          
          // Calculate watermark top-left position to center it at target
          let x = targetCenterX - (actualWidth / 2);
          let y = targetCenterY - (actualHeight / 2);
          
          // Ensure watermark stays within canvas bounds
          const minX = 0;
          const maxX = freshCanvas.width - actualWidth;
          const minY = 0;
          const maxY = freshCanvas.height - actualHeight;
          
          // Clamp to bounds while preserving both X and Y positions
          const originalX = x;
          const originalY = y;
          x = Math.max(minX, Math.min(maxX, x));
          y = Math.max(minY, Math.min(maxY, y));
          
          console.log(`[Watermark Canvas Debug] Canvas dimensions: ${freshCanvas.width}x${freshCanvas.height}`);
          console.log(`[Watermark Canvas Debug] Position strategy: ${positionY} (${positionY === 'center' ? '1/2 height' : '2/5 height'})`);
          console.log(`[Watermark Canvas Debug] Target center: (${targetCenterX}, ${targetCenterY})`);
          console.log(`[Watermark Canvas Debug] Original top-left: (${originalX}, ${originalY})`);
          console.log(`[Watermark Canvas Debug] Adjusted top-left: (${x}, ${y})`);
          
          console.log(`[Watermark Canvas Debug] Calculated position: (${x}, ${y}) with opacity ${opacity}`);
          
          // Check if position is within canvas bounds using actual size
          if (x < 0 || y < 0 || x + actualWidth > canvas.width || y + actualHeight > canvas.height) {
            console.warn(`[Watermark Canvas Debug] WARNING: Watermark position may be outside canvas bounds!`);
            console.warn(`[Watermark Canvas Debug] Canvas: ${canvas.width}x${canvas.height}, Watermark: ${x},${y} to ${x + actualWidth},${y + actualHeight}`);
          }
          
          // Save current state and log it
          ctx.save();
          console.log(`[Watermark Canvas Debug] Canvas context saved. Current globalAlpha: ${ctx.globalAlpha}`);
          
          // Set opacity and verify
          ctx.globalAlpha = opacity;
          console.log(`[Watermark Canvas Debug] Opacity set to: ${ctx.globalAlpha}`);
          
          // Calculate actual watermark center after bounds adjustment
          const actualCenterX = x + (actualWidth / 2);
          const actualCenterY = y + (actualHeight / 2);
          
          console.log(`[Watermark Canvas Debug] Actual watermark center: (${actualCenterX}, ${actualCenterY})`);
          
          // Draw watermark at user-specified position
          console.log(`[Watermark Canvas Debug] Drawing watermark at target position...`);
          console.log(`[Watermark Canvas Debug] Image complete: ${watermarkImg.complete}`);
          console.log(`[Watermark Canvas Debug] Image naturalWidth: ${watermarkImg.naturalWidth}`);
          console.log(`[Watermark Canvas Debug] Image naturalHeight: ${watermarkImg.naturalHeight}`);
          console.log(`[Watermark Canvas Debug] Image src length: ${watermarkImg.src.length}`);
          
          // Now try to draw the watermark
          console.log(`[Watermark Canvas Debug] Attempting to draw watermark image...`);
          try {
            ctx.drawImage(watermarkImg, x, y, actualWidth, actualHeight);
            console.log(`[Watermark Canvas Debug] drawImage call completed successfully`);
          } catch (error) {
            console.error(`[Watermark Canvas Debug] Error in drawImage:`, error);
          }
          
          // Verify watermark was drawn by checking center pixel
          const imageData = ctx.getImageData(actualCenterX, actualCenterY, 1, 1);
          const pixel = imageData.data;
          console.log(`[Watermark Canvas Debug] Watermark center pixel: rgba(${pixel[0]}, ${pixel[1]}, ${pixel[2]}, ${pixel[3]})`);
          
          // Restore state
          ctx.restore();
          console.log(`[Watermark Canvas Debug] Canvas context restored. Final globalAlpha: ${ctx.globalAlpha}`);
          
          // Capture after state for comparison
          const afterDataUrl = freshCanvas.toDataURL('image/png');
          console.log(`[Watermark Canvas Debug] AFTER: Fresh canvas data URL length: ${afterDataUrl.length}`);
          console.log(`[Watermark Canvas Debug] Canvas data changed: ${beforeDataUrl !== afterDataUrl}`);
          
          if (beforeDataUrl === afterDataUrl) {
            console.error('[Watermark Canvas Debug] ERROR: Fresh canvas data unchanged - watermark may not have been applied!');
            
            // Additional debugging for drawing failure
            console.log(`[Watermark Canvas Debug] Debugging drawing failure...`);
            console.log(`[Watermark Canvas Debug] Fresh canvas is valid: ${freshCanvas !== null}`);
            console.log(`[Watermark Canvas Debug] Context is valid: ${ctx !== null}`);
            console.log(`[Watermark Canvas Debug] Trying simple test draw...`);
            
            // Try a very simple draw to see if anything works
            ctx.fillStyle = 'red';
            ctx.fillRect(100, 100, 50, 50);
            const testDataUrl = freshCanvas.toDataURL('image/png');
            console.log(`[Watermark Canvas Debug] Test draw changed canvas: ${testDataUrl !== afterDataUrl}`);
            
          } else {
            console.log(`[Watermark Canvas Debug] SUCCESS: Fresh canvas data changed, watermark applied!`);
          }
          
          console.log('[Watermark Canvas Debug] Watermark application completed');
          resolve(freshCanvas);
        } catch (error) {
          console.error('[Watermark] Failed to draw watermark:', error);
          reject(new Error(`Failed to draw watermark: ${error}`));
        }
      };
      
      watermarkImg.onerror = (e) => {
        console.error('[Watermark] Failed to load watermark image:', e);
        reject(new Error('Failed to load watermark image'));
      };
      watermarkImg.src = watermarkDataUrl;
    });
  } catch (error) {
    console.error('[Watermark] Error in canvas watermark process:', error);
    console.warn('[Watermark] Falling back to original canvas without watermark');
    return canvas; // Return original canvas if watermark fails
  }
};

// Chart discovery utility (Single Responsibility Principle)
const discoverChartsInSection = (sectionElement: HTMLElement): ChartElement[] => {
  const chartSelectors = [
    'article[data-chart-title]',
    '.chart-card',
    '[data-chart]',
    'article:has(.chart-container)',
    'article:has([data-chart])'
  ];
  
  const chartElements: ChartElement[] = [];
  
  chartSelectors.forEach(selector => {
    const elements = sectionElement.querySelectorAll<HTMLElement>(selector);
    elements.forEach((element, index) => {
      // Avoid duplicates
      if (chartElements.some(chart => chart.element === element)) {
        return;
      }
      
      const title = element.dataset.chartTitle || 
                   element.querySelector('h3, h2, .chart-title')?.textContent ||
                   `chart-${chartElements.length + 1}`;
      
      const filename = title.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      
      chartElements.push({
        element,
        title: title.trim(),
        filename
      });
    });
  });
  
  return chartElements;
};

// Enhanced download function with canvas watermark (Open/Closed Principle - extends without modifying)
export const downloadChartWithCanvasWatermark = async (
  element: HTMLElement, 
  filename: string,
  options: DownloadOptions = {}
): Promise<void> => {
  console.log('Starting chart download with canvas watermark...', { filename });

  const {
    watermarkOptions = {},
    includeTimestamp = false,
    disableWatermark = false,
  } = options;

  // Detect which section this element belongs to for positioning strategy
  const detectSectionType = (element: HTMLElement): 'twoFifths' | 'center' | 'top' => {
    // Check if element itself is a section
    if (element.id === 'snapshot-charts-section' || element.id === 'overtime-charts-section') {
      return 'center'; // Snapshot view and overtime use center (1/2 height)
    }
    if (element.id === 'peer-comparison-charts-section') {
      return 'twoFifths'; // Peer comparison uses 2/5 height
    }
    
    // Check if element is within a section
    const snapshotSection = element.closest('#snapshot-charts-section');
    if (snapshotSection) {
      return 'center'; // Snapshot view uses center (1/2 height)
    }
    
    const peerSection = element.closest('#peer-comparison-charts-section');
    if (peerSection) {
      return 'twoFifths'; // Peer comparison uses 2/5 height
    }
    
    // Default fallback (for any unrecognized sections)
    return 'twoFifths';
  };

  // Enhanced watermark options with auto-detected positioning
  const enhancedWatermarkOptions = {
    ...watermarkOptions,
    positionY: watermarkOptions.positionY || detectSectionType(element)
  };

  // Wait for fonts
  if (document.fonts) {
    await document.fonts.ready;
    await new Promise(resolve => setTimeout(resolve, 200));
  }

  try {
    console.log('[Canvas Debug] Starting html2canvas capture...');
    // First, render the chart without watermark using existing logic
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: true, // Enable logging for canvas watermark debugging
      useCORS: true,
      onclone: async (clonedDoc: Document) => {
        console.log('[downloadChartWithCanvasWatermark] onclone callback started');
        
        // Simple approach: just remove headers and leave everything else exactly as is
        await processClonedElementAsync(clonedDoc, element, !disableWatermark);
        
        console.log('[downloadChartWithCanvasWatermark] onclone callback completed');
      },
    });

    console.log(`[Canvas Debug] html2canvas completed. Canvas dimensions: ${canvas.width}x${canvas.height}`);
    console.log(`[Canvas Debug] Canvas data URL length: ${canvas.toDataURL('image/png').length} characters`);

    // Now add watermark to the canvas if enabled
    console.log('[Canvas Debug] Applying watermark to canvas...');
    const watermarkedCanvas = disableWatermark
      ? canvas
      : await addCanvasWatermark(canvas, enhancedWatermarkOptions);
    
    console.log(`[Canvas Debug] Watermark applied. Final canvas dimensions: ${watermarkedCanvas.width}x${watermarkedCanvas.height}`);
    console.log(`[Canvas Debug] Final canvas data URL length: ${watermarkedCanvas.toDataURL('image/png').length} characters`);

    // Generate final filename
    const timestamp = includeTimestamp ? `-${new Date().toISOString().split('T')[0]}` : '';
    const finalFilename = `${filename}${timestamp}`;

    // Download the watermarked canvas
    const link = document.createElement('a');
    link.download = `${finalFilename}.png`;
    link.href = watermarkedCanvas.toDataURL('image/png');
    link.click();

    console.log('Chart with canvas watermark downloaded successfully');
  } catch (error) {
    console.error('Error during canvas watermark download:', error);
    throw error;
  }
};

// Simplified clone processing logic - just remove headers and leave everything else exactly as is
const processClonedElementSync = (
  clonedDoc: Document, 
  originalElement: HTMLElement, 
  addDOMWatermark: boolean = true
): void => {
  // Determine the correct root to operate on
  let clonedElement: HTMLElement | null = null;
  if (originalElement.id) {
    clonedElement = clonedDoc.body.querySelector(`#${originalElement.id}`) as HTMLElement | null;
  }
  if (!clonedElement) {
    clonedElement = clonedDoc.body.querySelector('article') as HTMLElement | null;
  }
  if (!clonedElement) {
    clonedElement = clonedDoc.body as HTMLElement;
  }

  // --- 1. REMOVE ONLY CHART HEADERS AND CONTROLS ---
  console.log('[processClonedElement] Removing chart headers only...');
  
  // Remove chart action button containers
  const allActionButtonContainers = clonedDoc.querySelectorAll('.chart-action-buttons');
  console.log(`[processClonedElement] Found ${allActionButtonContainers.length} chart action button containers to remove`);
  allActionButtonContainers.forEach((container, index) => {
    if (container instanceof HTMLElement) {
      container.remove();
      console.log(`[processClonedElement] Removed chart action button container ${index + 1}`);
    }
  });
  
  // Remove individual buttons by aria-label as backup
  const downloadButtons = clonedDoc.querySelectorAll('button[aria-label*="Download"]');
  const shareButtons = clonedDoc.querySelectorAll('button[aria-label*="Share"]');
  const aboutButtons = clonedDoc.querySelectorAll('button[aria-label*="About"]');
  const infoButtons = clonedDoc.querySelectorAll('button[aria-label*="Info"]');
  
  [...downloadButtons, ...shareButtons, ...aboutButtons, ...infoButtons].forEach((btn, index) => {
    if (btn instanceof HTMLElement) {
      btn.remove();
      console.log(`[processClonedElement] Removed button ${index + 1}: ${btn.getAttribute('aria-label')}`);
    }
  });
  
  // Remove snapshot bar chart tooltips specifically
  // Target tooltip containers that have opacity styling and contain tooltip-like content
  const snapshotTooltips = clonedDoc.querySelectorAll('div[style*="opacity"]');
  snapshotTooltips.forEach((tooltip, index) => {
    if (tooltip instanceof HTMLElement) {
      // Check if this is a tooltip by looking for tooltip-like content
      const hasTooltipContent = tooltip.querySelector('.bg-white.p-2.border.border-gray-200.rounded-lg.shadow-lg');
      if (hasTooltipContent) {
        tooltip.remove();
        console.log(`[processClonedElement] Removed snapshot tooltip ${index + 1}`);
      }
    }
  });
  
  // Also remove any tooltip cards directly by their styling
  const chartTooltipCards = clonedDoc.querySelectorAll('.bg-white.p-2.border.border-gray-200.rounded-lg.shadow-lg');
  chartTooltipCards.forEach((tooltipCard, index) => {
    if (tooltipCard instanceof HTMLElement) {
      tooltipCard.remove();
      console.log(`[processClonedElement] Removed chart tooltip card ${index + 1}`);
    }
  });
  
  console.log('[processClonedElement] Chart headers removed successfully');
  
  // --- 2. DISABLE TRANSITIONS AND ANIMATIONS ONLY ---
  // This is the only other change we make - disable transitions to prevent layout shifts during capture
  const descendants = clonedElement.getElementsByTagName('*');
  Array.from(descendants).forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.transition = 'none';
      el.style.animation = 'none';
    }
  });

  // --- 2.5. SELECTIVE POSITIONING FIXES FOR SPECIFIC ELEMENTS ---
  // Only fix elements that actually need positioning adjustments for download visibility
  
  // Fix category bubble text visibility and positioning
  const categoryBubbleTexts = clonedElement.querySelectorAll('.category-bubble-text');
  categoryBubbleTexts.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.display = 'inline-flex';
      el.style.alignItems = 'center';
      el.style.zIndex = '200';
      el.style.position = 'relative';
      el.style.top = '-8px'; // Shift up 8px
      el.style.overflow = 'visible';
      el.style.whiteSpace = 'nowrap';
    }
  });

  // Fix category bubble containers
  const categoryBubbles = clonedElement.querySelectorAll('.category-bubble');
  categoryBubbles.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.zIndex = '200';
      el.style.position = 'relative';
      el.style.top = '-8px'; // Shift up 8px
      el.style.overflow = 'visible';
    }
  });

  // Fix rank bubble text and containers
  const rankBubbleTexts = clonedElement.querySelectorAll('.rank-bubble-text');
  rankBubbleTexts.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.zIndex = '200';
      el.style.position = 'relative';
      el.style.top = '-8px'; // Shift up 8px
      el.style.overflow = 'visible';
    }
  });

  const rankBubbles = clonedElement.querySelectorAll('.rank-bubble');
  rankBubbles.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.zIndex = '200';
      el.style.position = 'relative';
      el.style.top = '0px'; // Shift up 8px
      el.style.overflow = 'visible';
    }
  });

  // Fix bar labels visibility and positioning
  const barLabels = clonedElement.querySelectorAll('.download-chart-label');
  barLabels.forEach(el => {
    if (el instanceof HTMLElement) {
      const computedStyle = getComputedStyle(el);
      const currentTop = parseFloat(computedStyle.top) || 0;
      el.style.position = 'absolute';
      el.style.top = `${currentTop - 4}px`; // Shift up 4px
      el.style.zIndex = '20';
      el.style.overflow = 'visible';
      el.style.whiteSpace = 'nowrap';
      el.style.opacity = '1';
    }
  });

  // Fix bar label shifts
  const barLabelShifts = clonedElement.querySelectorAll('.bar-label-shift');
  barLabelShifts.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.position = 'absolute';
      el.style.zIndex = '20';
      el.style.overflow = 'visible';
      el.style.whiteSpace = 'nowrap';
      el.style.opacity = '1';
    }
  });

  // Fix change percent value positioning
  const changePercentContainers = clonedElement.querySelectorAll('.flex.items-center.gap-2');
  changePercentContainers.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.position = 'relative';
      el.style.top = '20px'; // Shift down 20px
    }
  });

  // Fix text inside change percent bubble (shift up 8px)
  const changePercentTexts = clonedElement.querySelectorAll('.flex.items-center.gap-2 span');
  changePercentTexts.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.position = 'relative';
      el.style.top = '-3px'; // Shift up 4px
    }
  });

    // Fix snapshot metric card change percent spacing
  const tickerArrows = clonedElement.querySelectorAll('.ticker-arrow-icon');
    tickerArrows.forEach(el => {
      if (el instanceof HTMLElement) {
        el.style.position = 'relative';
        el.style.lineHeight = '1.2';
        el.style.verticalAlign = 'middle';
        // If rotated (down arrow) push it downward; otherwise pull up.
        el.style.top = el.classList.contains('rotate-180') ? '10px' : '-2px';
      }
    });

  // Fix snapshot metric card text elements that need shifting
  const chartTextShifts = clonedElement.querySelectorAll('.chart-text-shift');
  chartTextShifts.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.position = 'relative';
      el.style.lineHeight = '1.2';
      el.style.verticalAlign = 'middle';
      // Don't apply top shift to chart-text-shift elements - they already have proper positioning
    }
  });

  // Fix large value text (48px font-bold) in snapshot cards
  const largeValueTexts = clonedElement.querySelectorAll('span');
  largeValueTexts.forEach(el => {
    if (el instanceof HTMLElement) {
      const computedStyle = getComputedStyle(el);
      if (computedStyle.fontSize === '48px' && computedStyle.fontWeight === 'bold') {
        el.style.position = 'relative';
        el.style.top = '-16px'; // Apply upward shift to the value
        el.style.lineHeight = '1.2';
        el.style.verticalAlign = 'middle';
      }
    }
  });

  // Fix snapshot axis labels
  const snapshotAxisLabels = clonedElement.querySelectorAll('.snapshot-axis-label');
  snapshotAxisLabels.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.zIndex = '20';
      el.style.position = 'absolute';
      el.style.overflow = 'visible';
      el.style.whiteSpace = 'nowrap';
    }
  });

  // Fix any elements with calculated positions that might be hidden
  const elementsWithCalculatedPositions = clonedElement.querySelectorAll('[style*="top"], [style*="left"], [style*="transform"]');
  elementsWithCalculatedPositions.forEach(el => {
    if (el instanceof HTMLElement) {
      const computedStyle = getComputedStyle(el);
      // If element has calculated positioning, ensure it's visible
      if ((computedStyle.top && computedStyle.top !== 'auto' && !isNaN(parseFloat(computedStyle.top))) ||
          (computedStyle.left && computedStyle.left !== 'auto' && !isNaN(parseFloat(computedStyle.left)))) {
        el.style.zIndex = '20';
        el.style.overflow = 'visible';
        el.style.opacity = '1';
      }
    }
  });

  // --- 3. ADD WATERMARK IF REQUESTED ---
  const isSection = originalElement.id ? DEFAULT_SECTION_IDS.some(id => originalElement.id === id) : false;
  
  if (addDOMWatermark) {
    // Create watermark container
    const watermarkContainer = document.createElement('div');
    watermarkContainer.style.position = 'absolute';
    watermarkContainer.style.top = '0';
    watermarkContainer.style.left = '0';
    watermarkContainer.style.width = '100%';
    watermarkContainer.style.height = '100%';
    watermarkContainer.style.display = 'flex';
    watermarkContainer.style.alignItems = 'center';
    watermarkContainer.style.justifyContent = 'center';
    watermarkContainer.style.pointerEvents = 'none';
    watermarkContainer.style.zIndex = '9999999';

    // Create watermark image
    const watermarkImg = document.createElement('img');
    watermarkImg.src = DEFAULT_WATERMARK_URL;
    watermarkImg.style.width = isSection ? '400px' : '200px';
    watermarkImg.style.height = 'auto';
    watermarkImg.style.opacity = '0.5';
    watermarkImg.crossOrigin = 'anonymous';
    watermarkImg.style.maxWidth = 'none';
    watermarkImg.style.position = 'relative';

    // Add image to container
    watermarkContainer.appendChild(watermarkImg);

    // Add watermark to the clone element
    clonedElement.style.position = 'relative';
    clonedElement.appendChild(watermarkContainer);
  }
};

// Simplified async version - just remove headers and leave everything else exactly as is
const processClonedElementAsync = async (
  clonedDoc: Document, 
  originalElement: HTMLElement, 
  addDOMWatermark: boolean = true
): Promise<void> => {
  console.log('[processClonedElementAsync] Starting simplified chart header removal process...');
  
  // Remove chart action button containers
  const allActionButtonContainers = clonedDoc.querySelectorAll('.chart-action-buttons');
  console.log(`[processClonedElementAsync] Found ${allActionButtonContainers.length} chart action button containers to remove`);
  allActionButtonContainers.forEach((container, index) => {
    if (container instanceof HTMLElement) {
      container.remove();
      console.log(`[processClonedElementAsync] Removed chart action button container ${index + 1}`);
    }
  });
  
  // Wait for DOM to update
  await new Promise(resolve => setTimeout(resolve, 50));
  
  // Remove individual buttons by aria-label as backup
  const downloadButtons = clonedDoc.querySelectorAll('button[aria-label*="Download"]');
  const shareButtons = clonedDoc.querySelectorAll('button[aria-label*="Share"]');
  const aboutButtons = clonedDoc.querySelectorAll('button[aria-label*="About"]');
  const infoButtons = clonedDoc.querySelectorAll('button[aria-label*="Info"]');
  
  [...downloadButtons, ...shareButtons, ...aboutButtons, ...infoButtons].forEach((btn, index) => {
    if (btn instanceof HTMLElement) {
      btn.remove();
      console.log(`[processClonedElementAsync] Removed button ${index + 1}: ${btn.getAttribute('aria-label')}`);
    }
  });
  
  // Remove snapshot bar chart tooltips specifically
  // Target tooltip containers that have opacity styling and contain tooltip-like content
  const snapshotTooltips = clonedDoc.querySelectorAll('div[style*="opacity"]');
  snapshotTooltips.forEach((tooltip, index) => {
    if (tooltip instanceof HTMLElement) {
      // Check if this is a tooltip by looking for tooltip-like content
      const hasTooltipContent = tooltip.querySelector('.bg-white.p-2.border.border-gray-200.rounded-lg.shadow-lg');
      if (hasTooltipContent) {
        tooltip.remove();
        console.log(`[processClonedElementAsync] Removed snapshot tooltip ${index + 1}`);
      }
    }
  });
  
  // Also remove any tooltip cards directly by their styling
  const chartTooltipCards = clonedDoc.querySelectorAll('.bg-white.p-2.border.border-gray-200.rounded-lg.shadow-lg');
  chartTooltipCards.forEach((tooltipCard, index) => {
    if (tooltipCard instanceof HTMLElement) {
      tooltipCard.remove();
      console.log(`[processClonedElementAsync] Removed chart tooltip card ${index + 1}`);
    }
  });
  
  // Wait for DOM to update
  await new Promise(resolve => setTimeout(resolve, 50));
  
  console.log('[processClonedElementAsync] Chart headers removed successfully');
  
  // Apply the simplified sync processing
  processClonedElementSync(clonedDoc, originalElement, addDOMWatermark);
};

// Legacy async version for backward compatibility (if needed)
const processClonedElement = async (
  clonedDoc: Document, 
  originalElement: HTMLElement, 
  addDOMWatermark: boolean = true
): Promise<void> => {
  processClonedElementSync(clonedDoc, originalElement, addDOMWatermark);
};

// Helper function to check if an element contains text
const isTextElement = (element: HTMLElement): boolean => {
  const hasTextNode = Array.from(element.childNodes).some(
    node => node.nodeType === Node.TEXT_NODE && node.textContent?.trim() !== ''
  );
  return hasTextNode || element.tagName === 'P' || /^H[1-6]$/.test(element.tagName);
};





// Enhanced function to download all charts individually with watermarks (Main Feature)
export const downloadAllChartsWithWatermark = async (
  sectionId: string,
  sectionName: string,
  options: DownloadOptions = {}
): Promise<void> => {
  const {
    watermarkOptions = {},
    useCanvasWatermark = true,
    includeTimestamp = true
  } = options;

  console.log(`Starting download all charts for section: ${sectionId}`);

  // Wait for fonts to be fully loaded
  if (document.fonts) {
    await document.fonts.ready;
    await new Promise(resolve => setTimeout(resolve, 300));
  }

  try {
    const section = document.getElementById(sectionId);
    if (!section) {
      throw new Error(`Section with id '${sectionId}' not found`);
    }

    // Discover all charts within the section
    const chartElements = discoverChartsInSection(section);
    
    if (chartElements.length === 0) {
      console.warn(`No chart cards found in section: ${sectionId}`);
      return;
    }

    console.log(`Found ${chartElements.length} charts to download`);

    // Download each chart sequentially to avoid overwhelming the browser
    for (let i = 0; i < chartElements.length; i++) {
      const { element, title, filename } = chartElements[i];
      
      console.log(`Downloading chart ${i + 1}/${chartElements.length}: ${title}`);

      try {
        // Always use canvas-based watermark for consistency
        await downloadChartWithCanvasWatermark(element, filename, {
          watermarkOptions: {
            ...watermarkOptions,
            opacity: 0.5,
            size: watermarkOptions.size || { width: 1800, height: 540 } // Individual chart watermark size
          },
          includeTimestamp
        });
      } catch (error) {
        console.error(`Error downloading chart "${title}":`, error);
        // Continue with next chart even if one fails
      }

      // Small delay between downloads to prevent browser overwhelm
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    console.log('All charts downloaded successfully');
  } catch (error) {
    console.error('Error downloading all charts:', error);
    throw error;
  }
};

// Enhanced section download with better watermark control (Open/Closed Principle)
export const downloadChartSectionEnhanced = async (
  sectionElement: HTMLElement,
  filename: string,
  options: DownloadOptions = {}
): Promise<void> => {
  const {
    watermarkOptions = {},
    useCanvasWatermark = true,
    includeTimestamp = true
  } = options;

  console.log('Starting enhanced section download...');
  
  try {
    // Always use canvas-based watermark for consistency
    await downloadChartWithCanvasWatermark(sectionElement, filename, {
      watermarkOptions: {
        ...watermarkOptions,
        opacity: 0.5,
        size: watermarkOptions.size || { width: 3000, height: 900 } // Section watermark size
      },
      includeTimestamp
    });
  } catch (error) {
    console.error('Error in enhanced section download:', error);
    throw error;
  }
};

// Enhanced section download with canvas watermarks and debugging
export const downloadChartSection = async (
  sectionElement: HTMLElement,
  filename: string
): Promise<void> => {
  console.log('Starting chart section download with canvas watermarks...');
  
  // Use canvas watermarks for section downloads with larger watermark size
  await downloadChartWithCanvasWatermark(sectionElement, filename, {
    watermarkOptions: {
      opacity: 0.5,
      size: { width: 2000, height: 600 }, // Smaller watermark for section
      positionY: 'top',
    },
    includeTimestamp: false
  });
};
